import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './styles/connected-services.css';
import Badge from '../../components/Atoms/Badge';
import Button from '../../components/Atoms/Button';
import { Card, CardBody, CardHeader } from '../../components/Atoms/Card';
import { useSwal } from '../../components/Notifications/swal/SwalCom';
import {
  MSG,
  connectService,
  disconnectService,
  getProviders,
  recheckServices,
} from '../../services/authProviders';
import { readSession, useExportTargets, writeTargets } from '../../services/linkedServices';
import ConnectServiceDialog from './components/ConnectServiceDialog';

/**
 * Settings → Connected services.
 *
 * What a "connected service" is: another system (today ProWatchly) whose account
 * this workspace has proved it owns, which is what makes that system's sites
 * offerable as export targets on the model screens.
 *
 * The point this screen exists to make is that connecting is NORMALLY NOT AN
 * ACTION. Signing in to LabelFlow with a service's own email and password links
 * that service by itself — the sign-in cascade tries every configured provider
 * with what you typed and remembers the ones that answered. Connect is the
 * fallback for the one case the cascade cannot cover: your password on that
 * service is different from your LabelFlow password, so signing in here could
 * never have proved it. A screen that led with a Connect button would teach the
 * opposite, and people would go looking for a control they mostly do not need.
 *
 * Everything drawn here comes from the server. The browser caches the list the
 * last sign-in (or the last action on this screen) produced and MUST NOT be able
 * to invent a target: a corrupt cache reads as an empty list, never as "all
 * services available". The list is a hint for what to show — GPORTAL's own link
 * and approval remain the only authority for an export, so a stale row here
 * costs a clear error and can never cause an unauthorised deployment.
 *
 * Every live read or write on this screen needs the user's own LabelFlow
 * password, because the /services/* routes prove the actor with it before they
 * touch anything. That is why the first paint is the cache and not a fetch:
 * there is nothing this screen may ask the server for without a dialog.
 */

/* The server validates initials to 1-2 characters; this is the belt to that
   braces, because an unknown provider must render correctly with no code here. */
const monogramOf = (descriptor) =>
  String(descriptor?.initials || descriptor?.label || '?').trim().slice(0, 2).toUpperCase();

/* A custom property pointing at a token, not a colour — see connected-services.css. */
const accentStyleOf = (descriptor) => {
  const raw = Number(descriptor?.accentIndex);
  const index = Number.isFinite(raw) ? Math.min(5, Math.max(1, Math.round(raw))) : 1;
  return { '--consvc-accent': `var(--chart-${index})` };
};

/* The API serialises a UTC datetime and, on a target, sends it with NO offset
   ("2026-09-11T09:00:00"). `new Date()` reads an offset-less date-time as LOCAL,
   which put "last confirmed" five and a half hours in the future in
   Asia/Kolkata — on the one field somebody reads to decide whether to press
   Re-check. So a stamp carrying no zone is read as the UTC it is. */
const OFFSETLESS_ISO = /^\d{4}-\d{2}-\d{2}[T ][\d:]+(\.\d+)?$/;

const formatWhen = (value) => {
  if (!value) return '';
  const text = String(value);
  const parsed = new Date(OFFSETLESS_ISO.test(text) ? `${text.replace(' ', 'T')}Z` : text);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString();
};

/**
 * What to put on screen when a call did not succeed.
 *
 * `detail` is preferred and rendered verbatim wherever the server sent one: the
 * refusals that matter (a platform-admin account, a sub-user, a site already
 * claimed by another workspace) are written by the provider that knows why, and
 * a reason -> English map in the browser would go stale the day a provider is
 * added. The fallbacks below are only for the codes that carry no detail.
 */
const messageFor = (envelope) => {
  if (envelope?.detail) return envelope.detail;
  switch (envelope?.message) {
    case MSG.UNAUTHORISED:
      return 'That password was not accepted. Check your LabelFlow password, and the service password if you entered one.';
    case MSG.REFUSED:
      return 'That account cannot be used here.';
    case MSG.CLAIMED:
      return 'That site is already linked to another workspace.';
    case MSG.THROTTLED:
      return 'Too many attempts. Wait a few minutes and try again.';
    case MSG.BAD_REQUEST:
      return 'That service is not available on this deployment.';
    case MSG.NETWORK:
      return 'Could not reach the server.';
    default:
      return 'That did not work. Try again.';
  }
};

const ConnectedServices = () => {
  const { swalSuccess } = useSwal();
  const { targets, providers: cachedProviders, reload } = useExportTargets();

  /* readSession() is a synchronous localStorage read; holding it in state would
     only add a render. The user id is written at sign-in and is what every
     /services/* call is scoped to. */
  const session = useMemo(() => readSession() || {}, []);
  const userId = session.userId || '';

  const [fetchedProviders, setFetchedProviders] = useState(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [dialog, setDialog] = useState(null);   // { mode, providerKey, target }
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState('');

  /* One in-flight write at a time. `pending` is read out of the render closure,
     so two submits inside one frame (Enter held down, or Enter and then a click)
     both pass a state-only guard and fire the call twice — and each call spends
     a throttle unit, of which an identifier gets five per fifteen minutes. */
  const inFlight = useRef(false);

  /* The provider catalogue is the one thing here that needs no credential, so it
     is refreshed live: a provider enabled on the server should appear as
     connectable without the user having to sign out and back in. It is NOT
     written to the cache — the cache pairs providers with the targets they were
     computed against, and writing half of that pair would date the other half. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getProviders();
      if (cancelled) return;
      if (res?.message === MSG.OK && Array.isArray(res.data?.providers)) {
        setFetchedProviders(res.data.providers);
        setCatalogFailed(false);
        return;
      }
      /* Recorded, not swallowed. With no cached catalogue either, an unreported
         failure here makes the screen state as a FACT that this installation
         reaches no other system — which is the `.catch(() => [])` mistake: a
         broken call rendered as an empty list, and the reader believes it. */
      setCatalogFailed(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const providers = useMemo(() => {
    if (Array.isArray(fetchedProviders) && fetchedProviders.length) return fetchedProviders;
    return Array.isArray(cachedProviders) ? cachedProviders : [];
  }, [fetchedProviders, cachedProviders]);

  /* Read inside `applyPayload` without making the catalogue one of its
     dependencies — a payload arriving mid-render must see what is known now. */
  const providersRef = useRef(providers);
  providersRef.current = providers;

  /* Memoised, not a bare ternary: the `: []` branch builds a NEW array on every
     render, which changes the identity `connectedKeys` below depends on and makes
     that useMemo re-run every time — the exhaustive-deps warning is pointing at a
     real re-computation, not a style preference. */
  const rows = useMemo(() => (Array.isArray(targets) ? targets : []), [targets]);

  const descriptorFor = useCallback(
    (key) => providers.find((provider) => provider?.key === key) || null,
    [providers],
  );

  /* The home provider is the workspace itself, never a "connected service"; a
     provider that cannot export has no target to offer, so it is not listed
     either — there would be nothing to say about it. */
  const exportable = useMemo(
    () => providers.filter((provider) => provider && provider.canExport && !provider.isHome),
    [providers],
  );

  const connectedKeys = useMemo(
    () => new Set(rows.map((row) => row?.provider).filter(Boolean)),
    [rows],
  );

  const connectable = exportable.filter((p) => p.configured && !connectedKeys.has(p.key));
  const unavailable = exportable.filter((p) => !p.configured);

  const closeDialog = () => {
    if (pending) return;
    setDialog(null);
    setDialogError('');
  };

  /* A write answers with the WHOLE list, so the screen never has to guess what
     changed — it replaces the cache and redraws from it.

     `writeTargets` already announces itself to this tab (the browser's own
     `storage` event fires only in OTHER tabs, so the hook carries a custom event
     for the local one). `reload()` is still called explicitly: it is a no-op
     second read, and without it this screen would depend on a detail of another
     module for the one thing the user is watching for. */
  const applyPayload = useCallback((data) => {
    if (!data) return;
    const nextTargets = Array.isArray(data.targets) ? data.targets : [];
    /* The server sends both lists together, because they are one answer. If one
       ever arrives without the catalogue, the KNOWN catalogue is kept rather
       than overwritten with `[]`: writing an empty one would take away both
       "Available, not connected" and "Not available on this deployment", and
       the screen would then say this installation reaches nothing — from a call
       that had just succeeded. */
    const nextProviders = Array.isArray(data.providers) && data.providers.length
      ? data.providers
      : providersRef.current;
    writeTargets(nextTargets, nextProviders);
    if (nextProviders.length) {
      setFetchedProviders(nextProviders);
      setCatalogFailed(false);
    }
    reload();
  }, [reload]);

  const submitDialog = async ({ fields, actorPassword }) => {
    if (!dialog || pending || inFlight.current) return;
    const descriptor = descriptorFor(dialog.providerKey);
    const label = descriptor?.label || dialog.target?.label || 'the service';

    inFlight.current = true;
    setPending(true);
    setDialogError('');

    let res;
    if (dialog.mode === 'connect') {
      res = await connectService({
        userId,
        provider: dialog.providerKey,
        identifier: String(fields?.identifier || '').trim(),
        password: String(fields?.password || ''),
        actorPassword,
      });
    } else if (dialog.mode === 'disconnect') {
      res = await disconnectService({ userId, provider: dialog.providerKey, actorPassword });
    } else {
      res = await recheckServices({ userId, actorPassword });
    }
    inFlight.current = false;
    setPending(false);

    if (res?.message !== MSG.OK) {
      setDialogError(messageFor(res));
      return;
    }

    applyPayload(res.data);
    setDialog(null);

    if (dialog.mode === 'connect') {
      swalSuccess('<b>Connected</b>', `<p>${label} is linked to this workspace.</p>`);
    } else if (dialog.mode === 'disconnect') {
      swalSuccess(
        '<b>Disconnected</b>',
        `<p>The link was removed here. Nothing changed on ${label}.</p>`,
      );
    } else {
      const count = Array.isArray(res.data?.targets) ? res.data.targets.length : 0;
      swalSuccess(
        '<b>Services re-checked</b>',
        `<p>${count === 1 ? '1 service is' : `${count} services are`} linked to this workspace.</p>`,
      );
    }
  };

  if (!userId) {
    return (
      <div className="container consvc-page">
        <div className="col-md-12 mt-2">
          <div className="consvc-page__head">
            <h4 className="consvc-page__title mb-1">Connected services</h4>
          </div>
          <Card>
            <CardBody>
              <div className="consvc-empty">
                <strong>You are not signed in on this browser.</strong> Sign in again to see
                which services this workspace is connected to.
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container consvc-page">
      <div className="col-md-12 mt-2">
        <div className="consvc-page__head">
          <h4 className="consvc-page__title mb-1">Connected services</h4>
          <p className="consvc-page__subtitle mb-0">
            The systems this workspace has proved it owns an account on. A connected service is
            what makes its sites offerable as export targets when you deploy a trained model.
          </p>
        </div>
      </div>

      {/* The explainer comes FIRST and before any control, because the first
          thing to understand is that the control below is the exception. */}
      <div className="col-md-12 mt-2">
        <Card>
          <CardHeader title="How a service connects" />
          <CardBody>
            <div className="consvc-how">
              <div className="consvc-how__item consvc-how__item--key">
                <div className="consvc-how__title">
                  <i className="fa fa-sign-in-alt" aria-hidden="true"></i>
                  By signing in
                </div>
                <p className="consvc-how__text">
                  This is the normal way. When you sign in to LabelFlow, the email and password
                  you type are offered to every service this deployment knows about. Any service
                  that recognises them is linked, and re-confirmed at every sign-in.
                </p>
              </div>

              <div className="consvc-how__item">
                <div className="consvc-how__title">
                  <i className="fa fa-plug" aria-hidden="true"></i>
                  By connecting it here
                </div>
                <p className="consvc-how__text">
                  Only needed when your password on that service is different from your LabelFlow
                  password — signing in here could never have proved it. Connect asks for that
                  service&apos;s own credentials once.
                </p>
              </div>

              <div className="consvc-how__item">
                <div className="consvc-how__title">
                  <i className="fa fa-shield-alt" aria-hidden="true"></i>
                  What is kept
                </div>
                <p className="consvc-how__text">
                  A credential is used once, to prove the account is yours. It is never stored —
                  only the account id it proves is kept, which is what a link is.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="col-md-12 mt-2">
        <Card>
          <CardHeader
            title="Connected"
            actions={
              <div className="consvc-head-actions">
                <span className="consvc-section__count">
                  {rows.length === 1 ? '1 service' : `${rows.length} services`}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  tooltipText="Sign in to each service again and rebuild this list"
                  onClick={() => { setDialogError(''); setDialog({ mode: 'recheck', providerKey: '' }); }}
                >
                  <i className="fa fa-sync mr-1" aria-hidden="true"></i>Re-check
                </Button>
              </div>
            }
          />
          <CardBody>
            {rows.length ? (
              <div className="consvc-list">
                {rows.map((target) => {
                  const descriptor = target?.descriptor || descriptorFor(target?.provider) || {};
                  const label = target?.label || descriptor.label || target?.provider;
                  const viaSignin = target?.via !== 'connected';
                  const verified = formatWhen(target?.verifiedAt);
                  const unusable = target?.usable === false;
                  const stale = Boolean(target?.stale) && !unusable;
                  const needsEntitlement =
                    Boolean(descriptor.requiresEntitlement) && target?.entitlementState !== 'entitled';

                  const rowClass = [
                    'consvc-row',
                    unusable ? 'consvc-row--unusable' : '',
                    stale ? 'consvc-row--stale' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <div className={rowClass} key={`${target.provider}:${target.externalId}`}>
                      <div className="consvc-row__mono" style={accentStyleOf(descriptor)} aria-hidden="true">
                        {monogramOf(descriptor)}
                      </div>

                      <div className="consvc-row__body">
                        <div className="consvc-row__name">
                          <span>{label}</span>
                          {target?.name ? <span className="consvc-row__site">{target.name}</span> : null}
                          {/* Both chips report a STATE, so neither takes its colour
                              from the provider: a descriptor declaring
                              badgeVariant "danger" or "warning" would paint a
                              perfectly healthy link in the alarm colour, beside
                              rows where that colour means something is wrong. */}
                          {viaSignin
                            ? <Badge variant="success" icon="fa-sign-in-alt">Linked at sign-in</Badge>
                            : <Badge variant="info" icon="fa-plug">Connected here</Badge>}
                          {unusable ? <Badge variant="danger">Needs re-checking</Badge> : null}
                          {stale ? <Badge variant="warning">Not confirmed recently</Badge> : null}
                        </div>

                        <div className="consvc-row__meta">
                          {target?.email ? <>{target.email} · </> : null}
                          <span className="consvc-row__id">{target?.externalId}</span>
                          {verified ? <> · last confirmed {verified}</> : null}
                        </div>

                        {unusable ? (
                          <p className="consvc-row__note mb-0">
                            This link has not been confirmed for too long to be used as an export
                            target. Press Re-check, or sign in again with this service&apos;s
                            credentials, to confirm it is still yours.
                          </p>
                        ) : stale ? (
                          <p className="consvc-row__note mb-0">
                            Still on file, but not confirmed recently. Press Re-check to confirm it.
                          </p>
                        ) : null}

                        {viaSignin ? (
                          <p className="consvc-row__note mb-0">
                            Linked because you sign in with this account&apos;s own credentials, so
                            it relinks itself every time. There is nothing to disconnect.
                          </p>
                        ) : null}

                        {needsEntitlement && descriptor.entitlementHint ? (
                          <p className="consvc-row__note mb-0">
                            <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                            {descriptor.entitlementHint}
                          </p>
                        ) : null}
                      </div>

                      {/* Disconnect is offered ONLY for a link somebody made here.
                          A sign-in link IS the credentials: removing it would put
                          it straight back at the next sign-in, which is a dead
                          control wearing a live one's clothes. */}
                      {viaSignin ? null : (
                        <div className="consvc-row__actions">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            tooltipText={`Remove the link to ${label} — nothing changes on ${label}`}
                            onClick={() => {
                              setDialogError('');
                              setDialog({ mode: 'disconnect', providerKey: target.provider, target });
                            }}
                          >
                            Disconnect
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="consvc-empty">
                <strong>No service is connected to this workspace yet.</strong>{' '}
                {exportable.length ? (
                  <>
                    If you have an account on one of the services below, sign in to LabelFlow with
                    that account&apos;s email and password and it links itself. If your password
                    there is different, use Connect.
                  </>
                ) : catalogFailed ? (
                  /* The list of services could not be read. Saying instead that
                     this installation reaches nothing would state a failed call
                     as a fact about the deployment. */
                  <>
                    The list of services could not be read just now, so nothing can be offered to
                    connect to. Reload the page; if it keeps happening, tell whoever runs this
                    installation.
                  </>
                ) : (
                  <>
                    This installation is not set up to reach any other system, so there is nothing
                    to connect to yet.
                  </>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {connectable.length ? (
        <div className="col-md-12 mt-2">
          <Card>
            <CardHeader title="Available, not connected" />
            <CardBody>
              <p className="consvc-section__hint">
                This deployment can talk to these, but nothing has proved you own an account on
                them. Try signing in with that account first; connect it here only if its password
                differs from your LabelFlow one.
              </p>
              <div className="consvc-list">
                {connectable.map((descriptor) => (
                  <div className="consvc-row" key={descriptor.key}>
                    <div className="consvc-row__mono" style={accentStyleOf(descriptor)} aria-hidden="true">
                      {monogramOf(descriptor)}
                    </div>
                    <div className="consvc-row__body">
                      <div className="consvc-row__name">
                        <span>{descriptor.label}</span>
                        <Badge variant="neutral">Not connected</Badge>
                      </div>
                      <div className="consvc-row__meta">
                        {descriptor.exportNoun
                          ? `Connecting one offers its sites as an ${descriptor.exportNoun} target.`
                          : 'Connecting one offers its sites as an export target.'}
                      </div>
                      {descriptor.entitlementHint ? (
                        <p className="consvc-row__note mb-0">
                          <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                          {descriptor.entitlementHint}
                        </p>
                      ) : null}
                    </div>
                    <div className="consvc-row__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        tooltipText={`Prove you own a ${descriptor.label} account`}
                        onClick={() => {
                          setDialogError('');
                          setDialog({ mode: 'connect', providerKey: descriptor.key });
                        }}
                      >
                        <i className="fa fa-plug mr-1" aria-hidden="true"></i>
                        Connect {descriptor.label}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {unavailable.length ? (
        <div className="col-md-12 mt-2">
          <Card>
            <CardHeader title="Not available on this deployment" />
            <CardBody>
              {/* Listed rather than hidden: "why can I not send a model to X?" is
                  a question this screen should answer, and a service that is
                  simply absent reads as a fault in the screen. */}
              <p className="consvc-section__hint">
                LabelFlow knows about these, but this installation is not configured to reach
                them, so they cannot be connected from here. Ask whoever runs this installation.
              </p>
              <div className="consvc-list">
                {unavailable.map((descriptor) => (
                  <div className="consvc-row consvc-row--unavailable" key={descriptor.key}>
                    <div className="consvc-row__mono" aria-hidden="true">{monogramOf(descriptor)}</div>
                    <div className="consvc-row__body">
                      <div className="consvc-row__name">
                        <span>{descriptor.label}</span>
                        <Badge variant="neutral">Not configured here</Badge>
                      </div>
                      <div className="consvc-row__meta">
                        No connection to {descriptor.label} is set up on this installation.
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {dialog ? (
        <ConnectServiceDialog
          key={`${dialog.mode}:${dialog.providerKey}`}
          mode={dialog.mode}
          provider={descriptorFor(dialog.providerKey)}
          target={dialog.target || null}
          pending={pending}
          error={dialogError}
          onSubmit={submitDialog}
          onClose={closeDialog}
        />
      ) : null}
    </div>
  );
};

export default ConnectedServices;
